/**
 * Domain status enums — single source of truth shared between Drizzle schema
 * (apps/api/src/db/schema.ts) and frontend code (apps/web).
 *
 * If you add a value here, update the Drizzle CHECK constraint and any
 * pill / badge component variants that render the status.
 *
 * See docs/Build_Specification.md §3 for the original definitions.
 */

export const PCO_STATUSES = [
  'open',
  'active',
  'pending_approval',
  'approved',
  'paid',
  'void',
] as const;
export type PcoStatus = (typeof PCO_STATUSES)[number];

/**
 * How a PCO gets billed. ORGANIZATIONAL ONLY — this never touches money.
 *
 *   weekly_rollup — billed through the portal, week by week, off foreman
 *                   PDFs (the T&M flow). On 25132 that's 002 / 004 / 009 /
 *                   016 / 020 and nothing else today.
 *   lump_sum      — priced outside the portal and hard-entered here so the
 *                   PCO Log carries the funding picture. No weekly workflow,
 *                   no foreman blocks, no rollups.
 *
 * Default is `lump_sum` — the safe majority (all but the five listed above,
 * out of 67 parent PCOs as of 2026-07-28). See
 * docs/system/04-pcos-and-funding.md §Billing type.
 */
export const PCO_BILLING_TYPES = ['weekly_rollup', 'lump_sum'] as const;
export type PcoBillingType = (typeof PCO_BILLING_TYPES)[number];

export const ROLLUP_STATUSES = ['in_progress', 'submitted', 'approved', 'paid'] as const;
export type RollupStatus = (typeof ROLLUP_STATUSES)[number];

/**
 * Submission lifecycle (design 2026-07-08). A submission is the multi-week
 * billing package Coffman opens/closes by hand — PCO → submission → weekly
 * rollups. `approved` is the PCO Log's green "approved to bill" row; manual
 * flip in v1. Rollup-level statuses (ROLLUP_STATUSES) keep working underneath.
 */
export const SUBMISSION_STATUSES = ['open', 'closed', 'approved'] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

/**
 * Why a locked rollup got flagged as out of date post-submission. The DB
 * column (`weekly_rollups.stale_reason`) is plain text for flexibility;
 * this union is the canonical set the UI knows how to humanize and the
 * recompute paths know how to set.
 *
 *   late_pdf_received              — a foreman PDF for this week landed
 *                                    after the rollup was submitted
 *   code_remapped                  — an activity code mapped to this PCO
 *                                    was reassigned in Settings
 *   material_confirmed_after_submit — Joey confirmed a materials queue
 *                                    item belonging to this rollup after
 *                                    it was already locked
 *   block_reassigned_out           — a saved block was reassigned OUT of
 *                                    this rollup's PCO to a different one
 *                                    (this rollup is now over-billed until
 *                                    resubmitted). See ADR 0003.
 *   block_reassigned_in            — a saved block was reassigned INTO this
 *                                    rollup's PCO from a different one (this
 *                                    rollup is now under-billed until
 *                                    resubmitted). See ADR 0003.
 *   rate_changed                   — a project billing rate (markup %,
 *                                    equipment tax %, per-diem rate, or
 *                                    OT/DT mode) was changed in Settings
 *                                    after this rollup was submitted, so its
 *                                    frozen totals no longer match current
 *                                    rates. Reopen + resend to rebill.
 */
export const STALE_REASONS = [
  'late_pdf_received',
  'code_remapped',
  'material_confirmed_after_submit',
  'block_reassigned_out',
  'block_reassigned_in',
  'rate_changed',
] as const;
export type StaleReason = (typeof STALE_REASONS)[number];

/**
 * `parsed_with_warnings` is the soft-fail tier from the Slice A reliability work:
 * the persist layer wrote the children, but cross-field validation flagged
 * something worth Joey's attention (per-row total mismatch within tolerance,
 * footer mismatch within tolerance, foreman not on roster, etc.). The UI
 * surfaces these in the intake-health card with the specific warnings.
 * See docs/Build_Specification.md and apps/api/src/parser/cross-field.ts.
 */
export const PARSE_STATUSES = ['pending', 'parsed', 'parsed_with_warnings', 'failed'] as const;
export type ParseStatus = (typeof PARSE_STATUSES)[number];

export const MATERIAL_QUEUE_STATUSES = ['pending', 'confirmed', 'skipped', 'edited'] as const;
export type MaterialQueueStatus = (typeof MATERIAL_QUEUE_STATUSES)[number];

/**
 * Lifecycle of a single parsed activity-code block in the import inbox.
 *
 *   pending — parser wrote the row; awaiting Joey's review/save. NOT rolled
 *             up. Existing rows pre-2026-05-14 are backfilled to 'saved'
 *             since they had already entered rollups under the old flow.
 *   saved   — Joey committed the block (with or without per-line edits);
 *             confirmed_* columns on daily_labor_entries / daily_equipment_entries
 *             hold the snapshot. Contributes to rollups.
 *   skipped — Joey explicitly dropped the block (not relevant to billing,
 *             duplicate, etc.). Does not roll up.
 *   ignored — the block's activity code is on the IGNORE list (contract /
 *             lump-sum work that never bills through the portal). Set by the
 *             system, not by a per-block decision: intake stamps it when the
 *             code is ignored, and ignoring a code retroactively converts its
 *             still-`pending` blocks. Never reaches the PDF Inbox work queue
 *             and never rolls up. Reversed only by un-ignoring the CODE
 *             (Settings → PCOs → Ignored codes), which restores every
 *             'ignored' block to 'pending'.
 *
 * `saved` is the ONLY status that bills. `pending`, `skipped` and `ignored`
 * all contribute exactly nothing — every rollup-building query filters on
 * `block_status = 'saved'` positively, so a fourth non-saved status cannot
 * move money.
 */
export const BLOCK_STATUSES = ['pending', 'saved', 'skipped', 'ignored'] as const;
export type BlockStatus = (typeof BLOCK_STATUSES)[number];

/**
 * What unit the project's per-diem rate multiplies against.
 *
 *   st_hour    — legacy default; per-diem applies to ST hours only.
 *   total_hour — per-diem applies to ST + OT + DT (Coffman/Hoffman contract).
 *
 * Independent of `bill_overtime_as_st` on the projects table — a future
 * client could flat-rate ST + OT + DT billing but still cap per-diem at ST,
 * or vice versa.
 */
export const PER_DIEM_UNITS = ['st_hour', 'total_hour'] as const;
export type PerDiemUnit = (typeof PER_DIEM_UNITS)[number];

export const VARIANCE_STATUSES = [
  'open',
  'investigating',
  'resolved_recovered',
  'resolved_accepted',
  'disputed',
] as const;
export type VarianceStatus = (typeof VARIANCE_STATUSES)[number];

export const DELIVERY_STATUSES = ['sent', 'bounced', 'opened'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

export const USER_ROLES = ['admin', 'pm'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const PROJECT_ROLES = ['admin', 'contributor'] as const;
export type ProjectRole = (typeof PROJECT_ROLES)[number];

/**
 * What a single-use `auth_tokens` row is FOR.
 *
 *   invite         — first-password setup for a newly created user. TTL 72h so
 *                    it survives a weekend.
 *   password_reset — self-service reset from the login page. TTL 60 min.
 *
 * These are STRICTLY SINGLE-USE, unlike magic-link tokens (which are
 * deliberately replayable because Outlook Safe Links / Proofpoint detonate
 * URLs headlessly). The difference is that these links open a PAGE and the
 * token is consumed only by the POST that carries a new password — scanners
 * do not type passwords. That is also why they live in their own table.
 */
export const AUTH_TOKEN_PURPOSES = ['invite', 'password_reset'] as const;
export type AuthTokenPurpose = (typeof AUTH_TOKEN_PURPOSES)[number];

/**
 * How a session was established. Diagnostics, not authorization — its job is
 * to let the magic-link cutover PROVE nobody is still riding a magic-link
 * session before the code comes out.
 */
export const AUTH_METHODS = ['magic_link', 'password'] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

export const PROJECT_STATUSES = ['active', 'closed'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const DAYPARTS = ['day', 'swing'] as const;
export type Daypart = (typeof DAYPARTS)[number];

/**
 * Purpose of a project From-address. See memory: `project_resend_domains`.
 * Phase 0 uses `system` only; Phase 3 introduces `client_outbound`.
 */
export const FROM_ADDRESS_PURPOSES = ['system', 'client_outbound'] as const;
export type FromAddressPurpose = (typeof FROM_ADDRESS_PURPOSES)[number];

/**
 * Lifecycle of a user-authored feedback note (trial-week feedback feature).
 *   new       — just submitted, awaiting Brandon's review
 *   reviewed  — Brandon has read it (and maybe responded)
 *   shipped   — the feedback was acted on / fixed
 *   wont_fix  — acknowledged, intentionally not actioned
 * `reviewed_at` is stamped the first time status moves off 'new'.
 */
export const FEEDBACK_NOTE_STATUSES = ['new', 'reviewed', 'shipped', 'wont_fix'] as const;
export type FeedbackNoteStatus = (typeof FEEDBACK_NOTE_STATUSES)[number];
